import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Grid } from '@mui/material';
import TextFieldAdapter from '../../FinalFormAdapters/TextFieldAdapter';
import {
  composeValidators,
  validateBankAccount,
  validateRequired,
} from '../../../helpers/ValidatorHelper';
import { RegisterCreateRequest } from '../../../structures/request/RegisterCreateRequest';
import BicAutosuggestAdapter from '../../FinalFormAdapters/BicAutosuggestAdapter';
import { DadataBankResponse } from '../../../structures/response/dadata/DadataBankResponse';
import MfAlert from '../../UI/Alert/MfAlert';
import MfButton from '../../UI/Button/MfButton';
import { dadataBankResponseToCreateRequest } from './BankFormUtils';
import useClosableSnackbar from '../../../hooks/useClosableSnackbar';
import { errorToString } from '../../../helpers/Helper';
import { createBankDetails } from '../../../api/agent';
import useAccount from '../../../hooks/useAccount';
import EditableValue from './EditableValue';

type FormValues = Omit<RegisterCreateRequest, 'bankName' | 'title'> & {
  bankName?: string;
};

const BankForm = () => {
  const [processing, setProcessing] = useState(false);
  const { accountResponse, fetchAccountResponse } = useAccount();
  const [fallbackFormEnabled, setFallbackFormEnabled] = useState(false);
  const [bank, setBank] = useState<DadataBankResponse>(
    {} as DadataBankResponse
  );
  const { showSnackbar } = useClosableSnackbar();

  const bankDetails = accountResponse?.bankDetails;

  const submit = async (values: FormValues) => {
    let request: RegisterCreateRequest = {} as RegisterCreateRequest;
    const { number } = values;
    if (!fallbackFormEnabled && bank) {
      request = dadataBankResponseToCreateRequest(bank, '', number);
    } else {
      const { address = '', bik = '', correspondent, bankName = '' } = values;
      request = {
        title: '',
        number,
        bankName,
        address,
        bik,
        correspondent,
      };
    }
    setProcessing(true);
    try {
      await createBankDetails(request);
      setBank({} as DadataBankResponse);
      await fetchAccountResponse();
    } catch (e) {
      showSnackbar(errorToString(e));
    }
    setProcessing(false);
  };

  return (
    <Form
      initialValues={bankDetails ? { ...bankDetails } : null}
      onSubmit={submit}
      render={({ handleSubmit, submitting, pristine, values }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                name="number"
                label="Счёт"
                validate={composeValidators(
                  validateBankAccount,
                  validateRequired
                )}
                parse={(v: string) => (v.length > 20 ? v.substring(0, 20) : v)}
                component={TextFieldAdapter}
                required
              />
            </Grid>
            {!fallbackFormEnabled && (
              <Grid item xs={12}>
                <Field
                  name="bik"
                  label="Начните вводить название, БИК или адрес банка"
                  fullWidth
                  validate={validateRequired}
                  component={BicAutosuggestAdapter}
                  onItemSelected={(v: DadataBankResponse) => {
                    setBank(v);
                  }}
                  onDadataFailed={() => setFallbackFormEnabled(true)}
                  required
                />
                <MfAlert
                  severity={
                    (submitting || pristine) && !processing
                      ? 'success'
                      : 'warning'
                  }
                  sx={{ my: 2 }}
                >
                  <div>
                    <div>
                      Банк{' '}
                      <EditableValue
                        value={bankDetails?.bankName}
                        newValue={
                          bank?.name?.full ||
                          bank?.name?.short ||
                          bank?.name?.payment
                        }
                      />
                    </div>
                    <div>
                      Корр счёт{' '}
                      <EditableValue
                        value={bankDetails?.correspondent}
                        newValue={bank?.correspondent_account}
                      />
                    </div>
                    <div>
                      Адрес{' '}
                      <EditableValue
                        value={bankDetails?.address}
                        newValue={bank?.address?.value}
                      />
                    </div>
                    <div>
                      БИК{' '}
                      <EditableValue
                        value={bankDetails?.bik}
                        newValue={bank?.bic}
                      />
                    </div>
                    <div>
                      Счёт{' '}
                      <EditableValue
                        value={bankDetails?.number}
                        newValue={values?.number}
                      />
                    </div>
                  </div>
                </MfAlert>
              </Grid>
            )}
            {fallbackFormEnabled && (
              <>
                <Grid item xs={12}>
                  <Field
                    name="bik"
                    label="БИК"
                    fullWidth
                    validate={validateRequired}
                    component={TextFieldAdapter}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="bankName"
                    label="Название"
                    fullWidth
                    validate={validateRequired}
                    component={TextFieldAdapter}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="correspondent"
                    label="Корр. счёт"
                    fullWidth
                    validate={validateRequired}
                    component={TextFieldAdapter}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name="address"
                    label="Адрес"
                    fullWidth
                    validate={validateRequired}
                    component={TextFieldAdapter}
                    required
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <MfButton
                disabled={submitting || pristine}
                type="submit"
                variant="contained"
              >
                Сохранить банковские реквизиты
              </MfButton>
            </Grid>
          </Grid>
        </form>
      )}
    />
  );
};

export default BankForm;
